import Phaser from 'phaser';
import { BRAND, HEX } from '../config';
import { LEVELS } from '../game/levels';
import { formatTime, getBest } from '../game/scores';
import { presentLogical } from '../hidpi';

export class MenuScene extends Phaser.Scene {
  constructor() {
    super('MenuScene');
  }

  create() {
    const { width, height } = presentLogical(this);

    // Sky-to-ink background
    this.cameras.main.setBackgroundColor(BRAND.ink);
    const grad = this.add.graphics();
    grad.fillGradientStyle(BRAND.skyTop, BRAND.skyTop, BRAND.ink, BRAND.ink, 1, 1, 0, 0);
    grad.fillRect(0, 0, width, height);

    // Decorative sun in upper-right with spinning rays
    const sx = width - 110;
    const sy = 110;

    // Halo (pulses)
    const halo = this.add.graphics();
    for (let i = 6; i >= 1; i--) {
      halo.fillStyle(BRAND.amber, 0.06 * i);
      halo.fillCircle(0, 0, 28 + i * 6);
    }
    halo.setPosition(sx, sy);
    this.tweens.add({
      targets: halo,
      alpha: { from: 0.6, to: 1 },
      duration: 1600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Rays (slow rotation)
    const rays = this.add.graphics();
    rays.lineStyle(2, BRAND.amber, 0.65);
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      rays.lineBetween(Math.cos(a) * 48, Math.sin(a) * 48, Math.cos(a) * 68, Math.sin(a) * 68);
    }
    rays.setPosition(sx, sy);
    this.tweens.add({
      targets: rays,
      angle: 360,
      duration: 24000,
      repeat: -1,
      ease: 'Linear',
    });

    // Sun body (gentle scale pulse)
    const sun = this.add.graphics();
    sun.fillStyle(BRAND.amber, 1);
    sun.fillCircle(0, 0, 30);
    sun.fillStyle(BRAND.amberSoft, 0.7);
    sun.fillCircle(-6, -6, 6);
    sun.setPosition(sx, sy);
    this.tweens.add({
      targets: sun,
      scale: { from: 0.96, to: 1.04 },
      duration: 1800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Logo
    const logo = this.add.image(width / 2, 70, 'sj-logo');
    logo.setScale(46 / logo.height);

    // Title
    this.add
      .text(width / 2, 116, 'SOLAR INSTALLER', {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '40px',
        color: HEX.white,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setLetterSpacing(2);

    // Lockup line
    this.add
      .text(width / 2, 152, 'THE GAME', {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '13px',
        color: HEX.amber,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setLetterSpacing(6);

    // Tagline
    this.add
      .text(width / 2, 184, 'Place panels. Catch the sun. Hit the target.', {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '14px',
        color: HEX.amberSoft,
      })
      .setOrigin(0.5);

    // Red accent bar
    const accent = this.add.graphics();
    accent.fillStyle(BRAND.red, 1);
    accent.fillRect(width / 2 - 50, 210, 100, 3);

    this.add
      .text(width / 2, 232, 'SELECT A LEVEL', {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '11px',
        color: HEX.greyDim,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setLetterSpacing(2);

    const cardW = 170;
    const cardH = 156;
    const gap = 14;
    const totalW = LEVELS.length * cardW + (LEVELS.length - 1) * gap;
    const startX = (width - totalW) / 2;
    const cardY = 262;

    LEVELS.forEach((lvl, i) => {
      const cx = startX + i * (cardW + gap);
      const c = this.add.container(cx, cardY);

      const bg = this.add.graphics();
      this.paintCard(bg, cardW, cardH, false);
      c.add(bg);

      // Number badge
      const badge = this.add.graphics();
      badge.fillStyle(BRAND.red, 1);
      badge.fillRoundedRect(12, 12, 36, 20, 3);
      c.add(badge);
      const num = this.add
        .text(30, 22, `L${lvl.id}`, {
          fontFamily: 'Albert Sans, sans-serif',
          fontSize: '11px',
          color: HEX.white,
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      c.add(num);

      const ttl = this.add.text(12, 42, lvl.title.replace(/^\d+\s*[—-]\s*/, ''), {
        fontFamily: 'Albert Sans, sans-serif',
        fontSize: '17px',
        color: HEX.white,
        fontStyle: 'bold',
        wordWrap: { width: cardW - 24 },
      });
      c.add(ttl);

      // Stat row
      const statY = cardH - 60;
      const statG = this.add.graphics();
      statG.lineStyle(1, 0xffffff, 0.08);
      statG.lineBetween(12, statY - 6, cardW - 12, statY - 6);
      c.add(statG);

      c.add(
        this.add.text(12, statY, 'TARGET', {
          fontFamily: 'Albert Sans, sans-serif',
          fontSize: '9px',
          color: HEX.greyDim,
          fontStyle: 'bold',
        }),
      );
      c.add(
        this.add.text(12, statY + 12, `${lvl.targetKW.toFixed(1)} kW`, {
          fontFamily: 'Albert Sans, sans-serif',
          fontSize: '15px',
          color: HEX.amber,
          fontStyle: 'bold',
        }),
      );
      c.add(
        this.add
          .text(cardW - 12, statY, 'BUDGET', {
            fontFamily: 'Albert Sans, sans-serif',
            fontSize: '9px',
            color: HEX.greyDim,
            fontStyle: 'bold',
          })
          .setOrigin(1, 0),
      );
      c.add(
        this.add
          .text(cardW - 12, statY + 12, `$${lvl.budget}`, {
            fontFamily: 'Albert Sans, sans-serif',
            fontSize: '15px',
            color: HEX.white,
            fontStyle: 'bold',
          })
          .setOrigin(1, 0),
      );

      // Best time chip at top-right of card (only if recorded)
      const best = getBest(lvl.id);
      if (best != null) {
        const chipText = `BEST ${formatTime(best)}`;
        const tmp = this.add
          .text(0, 0, chipText, { fontFamily: 'Albert Sans, sans-serif', fontSize: '9px' })
          .setVisible(false);
        const cw = tmp.width + 10;
        tmp.destroy();
        const ch = 14;
        const cxr = cardW - cw - 10;
        const cyr = 12;
        const chipBg = this.add.graphics();
        chipBg.fillStyle(BRAND.amber, 0.15);
        chipBg.fillRoundedRect(cxr, cyr, cw, ch, 3);
        chipBg.lineStyle(1, BRAND.amber, 0.7);
        chipBg.strokeRoundedRect(cxr + 0.5, cyr + 0.5, cw - 1, ch - 1, 3);
        c.add(chipBg);
        c.add(
          this.add
            .text(cxr + cw / 2, cyr + ch / 2, chipText, {
              fontFamily: 'Albert Sans, sans-serif',
              fontSize: '9px',
              color: HEX.amber,
              fontStyle: 'bold',
            })
            .setOrigin(0.5)
            .setLetterSpacing(1),
        );
      }

      bg.setInteractive(
        new Phaser.Geom.Rectangle(0, 0, cardW, cardH),
        Phaser.Geom.Rectangle.Contains,
      );
      bg.on('pointerover', () => {
        this.paintCard(bg, cardW, cardH, true);
        c.setScale(1.03);
      });
      bg.on('pointerout', () => {
        this.paintCard(bg, cardW, cardH, false);
        c.setScale(1);
      });
      bg.on('pointerdown', () => {
        this.scene.start('BriefingScene', { levelIndex: i });
      });
    });

    this.add
      .text(
        width / 2,
        height - 24,
        'Click to place · R to rotate · Shift+click to remove · Finish Job to score',
        {
          fontFamily: 'Albert Sans, sans-serif',
          fontSize: '12px',
          color: HEX.greyDim,
        },
      )
      .setOrigin(0.5);
  }

  private paintCard(g: Phaser.GameObjects.Graphics, w: number, h: number, hover: boolean) {
    g.clear();
    g.fillStyle(hover ? BRAND.surface : BRAND.inkSoft, 1);
    g.fillRoundedRect(0, 0, w, h, 10);
    g.lineStyle(1, hover ? BRAND.amber : 0xffffff, hover ? 0.9 : 0.08);
    g.strokeRoundedRect(0.5, 0.5, w - 1, h - 1, 10);
  }
}
